// v184 admin branding page — upload + delete the deployment logo
// that replaces the built-in Wordmark in every AppShell header.
//
// Single-tenant per deployment. Upload form is a plain HTML file
// input + submit button; preview shows the current logo via the
// public-serving endpoint /branding/logo. Delete button reverts to
// the built-in mark.

import { useRef, useState } from 'react';
import { Image, Trash2, Upload } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { usePublicConfig } from '../../api/publicConfig';

const MAX_BYTES = 256 * 1024;
const ACCEPTED = 'image/png,image/jpeg,image/svg+xml,image/webp';

// Pull the readable validator message out of the JSON error envelope
// the backend returns for 4xx responses ({"error": "<text>"}). Falls
// back to the raw body if the response isn't JSON, which is what the
// previous implementation showed.
async function readErr(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed.error === 'string') {
      return `${res.status}: ${parsed.error}`;
    }
  } catch {
    // not JSON — fall through to raw text
  }
  return `${res.status}: ${text}`;
}

export function AdminBranding() {
  const { data: pubConfig, mutate } = usePublicConfig();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const onUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    const f = fileRef.current?.files?.[0];
    if (!f) return;
    if (f.size > MAX_BYTES) {
      setErr(`Logo too large (${(f.size / 1024).toFixed(1)} KiB) — limit is 256 KiB.`);
      return;
    }
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('logo', f);
      const res = await fetch('/api/admin/branding/logo', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(await readErr(res));
      setMsg('Logo updated. Refresh any open tabs to see the new mark.');
      if (fileRef.current) fileRef.current.value = '';
      await mutate();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!confirm('Revert to the built-in OrbitalReg wordmark?')) return;
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const res = await fetch('/api/admin/branding/logo', {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(await readErr(res));
      setMsg('Custom logo cleared. Built-in wordmark restored.');
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const hasLogo = !!pubConfig?.logo_url;

  return (
    <AdminShell
      title="Branding"
      description="Replace the built-in OrbitalReg wordmark with your own logo. Single image, ≤ 256 KiB, PNG / JPEG / SVG / WEBP."
    >
      <Card>
        <CardHeader title="Current logo" />
        <CardBody>
          {hasLogo ? (
            <div className="flex items-center gap-4 p-4 rounded border border-border bg-bg-inset/40">
              <img
                src={pubConfig.logo_url}
                alt="Current branding logo"
                className="h-12 max-w-[280px] object-contain bg-bg-base rounded p-2"
              />
              <div className="flex-1 text-sm text-fg-muted">
                Active across every page in the app.
              </div>
              <Button variant="secondary" disabled={busy} onClick={onDelete}>
                <Trash2 className="h-4 w-4" />
                Remove
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-sm text-fg-muted">
              <Image className="h-5 w-5 text-fg-subtle" />
              No custom logo configured. Built-in OrbitalReg wordmark is in use.
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="mt-4">
        <CardHeader
          title="Upload new logo"
          description="Replaces the existing logo immediately. Browsers may cache the previous version up to an hour — do a hard reload to verify."
        />
        <CardBody>
          <form onSubmit={onUpload} className="space-y-4">
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPTED}
              required
              className="text-sm text-fg file:mr-3 file:rounded file:border file:border-border file:bg-bg-inset file:px-3 file:py-1.5 file:text-fg hover:file:bg-bg-hover"
            />
            <p className="text-xs text-fg-muted">
              Tips: SVG renders sharply at every size. For raster formats,
              ~280×60 looks best (raster uploads must be between 16×16
              and 4096×1024 px). The image is shown in both light + dark
              themes — a logo with a transparent background works on both.
              SVG uploads are checked for embedded scripts and rejected
              if any are present.
            </p>
            <div className="flex items-center gap-3">
              <Button variant="primary" type="submit" disabled={busy}>
                <Upload className="h-4 w-4" />
                {busy ? 'Uploading…' : 'Upload'}
              </Button>
              {msg && <span className="text-xs text-success">{msg}</span>}
              {err && <span className="text-xs text-danger font-mono">{err}</span>}
            </div>
          </form>
        </CardBody>
      </Card>
    </AdminShell>
  );
}
